import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../services/api';
import {
  FiMail,
  FiPhone,
  FiSmartphone,
  FiGlobe,
  FiMapPin,
  FiDownload,
  FiBriefcase,
  FiFacebook,
  FiInstagram,
  FiGithub,
} from 'react-icons/fi';
import { FaXTwitter } from 'react-icons/fa6';
import './PublicCard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const ICONS = `${process.env.PUBLIC_URL}/icons`;

// Open WeChat app (same behaviour as WhatsApp); fall back to the app store.
const openWeChat = () => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const storeUrl = isIOS
    ? 'https://apps.apple.com/app/wechat/id414478124'
    : 'https://play.google.com/store/apps/details?id=com.tencent.mm';
  let opened = false;
  const onHide = () => {
    if (document.hidden) opened = true;
  };
  document.addEventListener('visibilitychange', onHide);
  window.location.href = 'weixin://';
  setTimeout(() => {
    document.removeEventListener('visibilitychange', onHide);
    if (!opened) window.location.href = storeUrl;
  }, 1500);
};

const resolveAssetUrl = (url) => {
  if (!url) return null;
  if (typeof url !== 'string') return null;
  if (url.startsWith('http')) return url;
  const base = API_URL.replace(/\/$/, '');
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
};

const PublicCard = () => {
  const { cardId, slug } = useParams();
  const id = cardId || slug;
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    loadCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (card) {
      // Update page title with user name and company
      const title = card.fullName + (card.businessName ? ` - ${card.businessName}` : '');
      document.title = title;

      // Update favicon with user avatar
      if (card.avatarUrl) {
        const link = document.querySelector("link[rel~='icon']");
        if (link) {
          link.href = card.avatarUrl;
        } else {
          const newLink = document.createElement('link');
          newLink.rel = 'icon';
          newLink.href = card.avatarUrl;
          document.head.appendChild(newLink);
        }
      }
    }
  }, [card]);

  const loadCard = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPublicCard(id);

      if (response.ok && response.card) {
        const c = response.card;

        // Parse social links (object or JSON string)
        let socialLinks = null;
        const rawSocial = c.social_links ?? c.socialLinks;
        if (rawSocial) {
          try {
            socialLinks = typeof rawSocial === 'string' ? JSON.parse(rawSocial) : rawSocial;
          } catch (e) {
            console.error('Error parsing social links:', e);
          }
        }

        setCard({
          fullName: c.full_name ?? c.fullName ?? '',
          businessName: c.business_name ?? c.businessName ?? '',
          jobTitle: c.job_title ?? c.jobTitle ?? '',
          email: c.email ?? '',
          phoneNumber: c.phone_number ?? c.phoneNumber ?? '',
          cellphone: c.cellphone ?? '',
          whatsapp: c.whatsapp ?? '',
          wechat: c.wechat ?? '',
          website: c.website ?? '',
          address: c.address ?? '',
          avatarUrl: resolveAssetUrl(c.avatar_url ?? c.avatarUrl),
          businessLogoUrl: resolveAssetUrl(c.business_logo_url ?? c.businessLogo),
          slug: c.slug ?? c.public_url_slug ?? id,
          socialLinks,
        });
      } else {
        setError('Card not found or is not publicly accessible');
      }
    } catch (err) {
      console.error('Error loading card:', err);
      setError('Card not found or is not publicly accessible');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = () => {
    if (!card) return;

    const vCard = `BEGIN:VCARD
VERSION:3.0
FN:${card.fullName || ''}
TITLE:${card.jobTitle || ''}
ORG:${card.businessName || ''}
EMAIL:${card.email || ''}
TEL;TYPE=WORK:${card.phoneNumber || ''}
TEL;TYPE=CELL:${card.cellphone || ''}
URL:${card.website || ''}
ADR:;;${card.address || ''};;;;
END:VCARD`;

    const blob = new Blob([vCard], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${card.fullName || 'contact'}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="public-card-container">
        <div className="loading">Loading card...</div>
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="public-card-container">
        <div className="error-card">
          <h2>Card Not Found</h2>
          <p>{error || 'This card does not exist or is not publicly accessible.'}</p>
        </div>
      </div>
    );
  }

  const social = card.socialLinks || {};
  const hasSocial =
    social.linkedin ||
    social.twitter ||
    social.facebook ||
    social.instagram ||
    social.github ||
    social.whatsapp ||
    social.wechat ||
    social.tiktok;

  const role = `${card.jobTitle || 'CEO'} @ ${card.businessName || 'Omni'}`;
  const profileLink = `https://my-omni.link/${card.slug || 'user'}`;

  return (
    <div className="public-card-container">
      <div className="public-card-wrapper">
        {/* Profile Card */}
        <div
          className="profile-card"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/ProfileCard.png)` }}
        >
          {/* Avatar */}
          <div className="avatar-ring">
            {card.avatarUrl && !avatarError ? (
              <img
                src={card.avatarUrl}
                alt={card.fullName}
                className="public-card-avatar"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <div className="avatar-placeholder">
                <img src="/logo.png" alt="Omni" className="avatar-logo" />
              </div>
            )}
          </div>

          <div className="profile-name">{card.fullName || 'User'}</div>

          <div className="profile-divider" />

          <div className="profile-role">{role}</div>
        </div>

        {/* Details Card */}
        <div className="details-card">
          <div className="details-sidebar" />

          {card.phoneNumber && (
            <a className="detail-row" href={`tel:${card.phoneNumber}`}>
              <span className="detail-icon"><FiPhone size={20} color="#4A4A4A" /></span>
              <span className="detail-text">{card.phoneNumber}</span>
            </a>
          )}

          {card.cellphone && (
            <a className="detail-row" href={`tel:${card.cellphone}`}>
              <span className="detail-icon"><FiSmartphone size={20} color="#4A4A4A" /></span>
              <span className="detail-text">{card.cellphone}</span>
            </a>
          )}

          {card.whatsapp && (
            <a
              className="detail-row"
              href={`https://wa.me/${card.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="detail-icon"><img src={`${ICONS}/whatsapp.png`} alt="WhatsApp" className="detail-img-icon" /></span>
              <span className="detail-text">{card.whatsapp}</span>
            </a>
          )}

          {card.wechat && (
            <div className="detail-row" role="button" tabIndex={0} onClick={openWeChat}>
              <span className="detail-icon"><img src={`${ICONS}/wechat.png`} alt="WeChat" className="detail-img-icon" /></span>
              <span className="detail-text">{card.wechat}</span>
            </div>
          )}

          {card.businessName && (
            <div className="detail-row">
              <span className="detail-icon"><FiBriefcase size={20} color="#4A4A4A" /></span>
              <span className="detail-text">{card.businessName}</span>
            </div>
          )}

          {card.email && (
            <a className="detail-row" href={`mailto:${card.email}`}>
              <span className="detail-icon"><FiMail size={20} color="#4A4A4A" /></span>
              <span className="detail-text">{card.email}</span>
            </a>
          )}

          {card.website && (
            <a
              className="detail-row"
              href={card.website.startsWith('http') ? card.website : `https://${card.website}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="detail-icon"><FiGlobe size={20} color="#4A4A4A" /></span>
              <span className="detail-text">{card.website}</span>
            </a>
          )}

          {card.address && (
            <a
              className="detail-row"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(card.address)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="detail-icon"><FiMapPin size={20} color="#4A4A4A" /></span>
              <span className="detail-text">{card.address}</span>
            </a>
          )}

          <a className="detail-row" href={profileLink} target="_blank" rel="noopener noreferrer">
            <span className="detail-icon">
              <img src="/logo.png" alt="Omni" className="detail-omni-icon" />
            </span>
            <span className="detail-text">{profileLink}</span>
          </a>

          {hasSocial && (
            <div className="social-section">
              {social.linkedin && (
                <a className="social-btn" style={{ background: '#0077B5' }} href={social.linkedin} target="_blank" rel="noopener noreferrer" title="LinkedIn">
                  <img src={`${ICONS}/linkedin.png`} alt="LinkedIn" className="social-img" />
                </a>
              )}
              {social.twitter && (
                <a className="social-btn" style={{ background: '#000000' }} href={social.twitter} target="_blank" rel="noopener noreferrer" title="X">
                  <FaXTwitter size={18} color="#FFFFFF" />
                </a>
              )}
              {social.facebook && (
                <a className="social-btn" style={{ background: '#1877F2' }} href={social.facebook} target="_blank" rel="noopener noreferrer" title="Facebook">
                  <FiFacebook size={20} color="#FFFFFF" />
                </a>
              )}
              {social.instagram && (
                <a className="social-btn" style={{ background: '#E4405F' }} href={social.instagram} target="_blank" rel="noopener noreferrer" title="Instagram">
                  <FiInstagram size={20} color="#FFFFFF" />
                </a>
              )}
              {social.github && (
                <a className="social-btn" style={{ background: '#24292E' }} href={social.github} target="_blank" rel="noopener noreferrer" title="GitHub">
                  <FiGithub size={20} color="#FFFFFF" />
                </a>
              )}
              {social.whatsapp && (
                <a className="social-btn" style={{ background: '#25D366' }} href={`https://wa.me/${social.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" title="WhatsApp">
                  <img src={`${ICONS}/whatsapp.png`} alt="WhatsApp" className="social-img social-img-white" />
                </a>
              )}
              {social.wechat && (
                <span className="social-btn" style={{ background: '#09B83E' }} title="WeChat" role="button" tabIndex={0} onClick={openWeChat}>
                  <img src={`${ICONS}/wechat.png`} alt="WeChat" className="social-img social-img-white" />
                </span>
              )}
              {social.tiktok && (
                <a className="social-btn" style={{ background: '#000000' }} href={social.tiktok} target="_blank" rel="noopener noreferrer" title="TikTok">
                  <img src={`${ICONS}/tiktok.png`} alt="TikTok" className="social-img" />
                </a>
              )}
            </div>
          )}
        </div>

        <button className="save-btn" onClick={handleSaveContact}>
          <FiDownload size={18} />
          Save to Contacts
        </button>

        <div className="footer">
          <div className="download-buttons">
            <a href="https://play.google.com/store/apps/details?id=com.omnicards.app&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="store-button">
              <img src="/Google_Play.png" alt="Get it on Google Play" className="store-badge" />
            </a>
            <a href="https://apps.apple.com/app/omni/id1234567890" target="_blank" rel="noopener noreferrer" className="store-button">
              <img src="/Apple_Store.png" alt="Download on the App Store" className="store-badge" />
            </a>
          </div>
          <p><img src="/web_icon.png" alt="OMNI" className="footer-logo" /> Powered by <strong>OMNI</strong></p>
        </div>
      </div>
    </div>
  );
};

export default PublicCard;